import { ArtifactBiscuit } from "@/components/artifacts/ArtifactBiscuit.tsx";
import { OmniArtifactFormCard } from "@/components/artifacts/OmniArtifactFormCard.tsx";
import { useMessageList } from "@/components/messages/hooks/useMessageList.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { useSubmitForm } from "@/hooks/useArtifacts.tsx";
import { apiErrorMessage } from "@/lib/http.ts";
import { toPlainText } from "@/shared/markdownPlainText.ts";
import { lastAgentText } from "@/shared/lastAgentText.ts";
import { CONTINUE_IN_THREAD } from "@/shared/taskRowCopy.ts";
import { isUnansweredForm, selectTurnOmniCards } from "@/shared/selectOmniArtifacts.ts";
import { isActionableArtifact } from "@/types/artifacts.ts";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";

/** What the reply field asks for, which is the shortest true description of it. */
const REPLY_HINT = "Type your answer…";

/**
 * A form's answers so far, with the form they answer. Keyed, because the row
 * that keeps the draft outlives the question: answered elsewhere, the next
 * question would otherwise open on the last one's half-written answers.
 */
export type FormDraft = { formId: string; values: Record<string, string> };
const SEND = "Send";

/**
 * Three lines of what okthx last said, as native shows it. The whole message
 * is the thread's to show: a research answer ran to a screen of text here,
 * with the question it ended on scrolled out of sight.
 */
const WHY_TEXT = "line-clamp-3 text-sm text-muted-foreground";

/**
 * The way to the whole conversation, from whichever answer the dialog is
 * showing. Native's dialog offers the same button in the same place; the
 * dialog closes first, so the thread is not opened under an overlay.
 */
const ContinueInThread = ({ threadId, onClose }: { threadId: number; onClose: () => void }) => {
  const [, navigate] = useLocation();
  return (
    <div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-statsig-name="tasks-answer-continue-in-thread"
        onClick={() => {
          onClose();
          navigate(`/threads/${threadId}`);
        }}
      >
        {CONTINUE_IN_THREAD}
      </Button>
    </div>
  );
};

/**
 * Okthx's open questions about a task, answered without leaving the list.
 *
 * The port of native's dialog, and deliberately the same shape: what okthx is
 * waiting on, then the other true things a reader can say about a task they
 * have been asked about. Answered here rather than by navigating to the
 * thread, which would answer the question by throwing away the reader's place
 * in the list they were working.
 *
 * It submits through `useSubmitForm`, the same mutation the canvas's
 * `AskUserForm` uses, rather than through the thread composer's review
 * controller: that one is built around a queue of artifacts with pagination
 * and batched decisions, none of which a single form has. One form, one
 * endpoint, already shared by two surfaces — this is the third.
 *
 * Closed once an answer has landed, never on the send: closed on the send, a
 * failed answer vanished with the dialog and left the question standing.
 */
export const TaskAnswerDialog = ({
  title,
  threadId,
  draft,
  onDraft,
  onClose,
  onAnswered,
}: {
  title: string;
  threadId: number;
  /**
   * A form's answers so far, held by the row rather than here: the dialog
   * closes to get out of the canvas's way on Expand, and a draft kept in it
   * would go with it. The row hands the same draft back when it reopens, and
   * only a draft of this form is read.
   */
  draft: FormDraft | null;
  onDraft: (draft: FormDraft) => void;
  onClose: () => void;
  /** The questions are answered, so okthx is unblocked and the row has moved on. */
  onAnswered: () => void;
}) => {
  const [reply, setReply] = useState("");
  const { items, isPending, isSending, sendError, sendMessage } = useMessageList({ threadId });

  /**
   * What the thread is waiting on, whatever kind it is — the same gate every
   * other "waiting for user approval" surface asks.
   */
  const pending = useMemo(() => selectTurnOmniCards(items).find(isActionableArtifact), [items]);
  const form = pending && isUnansweredForm(pending) ? pending : undefined;
  const approval = pending?.isHeavy && pending.biscuit ? pending : undefined;
  const why = useMemo(() => lastAgentText(items), [items]);

  const submitForm = useSubmitForm({ threadId, messageId: form?.messageId ?? 0 });

  // The biscuit fires its own actions and refetches the thread, so the
  // approval leaving the thread's open questions is the answer having landed,
  // and the dialog's cue to go.
  const hadApproval = useRef(false);
  useEffect(() => {
    if (approval) hadApproval.current = true;
    else if (hadApproval.current) onAnswered();
  }, [approval, onAnswered]);

  const written = reply.trim();
  const sendReply = async () => {
    // An empty answer is not an answer: posting a blank turn would wake okthx
    // to read nothing.
    if (!written || isSending) return;
    if (await sendMessage(written)) onAnswered();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      {/* Bounded and scrolling: a form draws its question and every option at
          full height, and a long one would otherwise push its own controls
          off a fixed, centred dialog. */}
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base">{title}</DialogTitle>
        </DialogHeader>

        {/* The thread is read on open, so for a moment there is nothing to
            show: a shape where the question will be, rather than an empty box
            that reads as having nothing to ask. */}
        {isPending && <Skeleton className="h-20 w-full" />}

        {!isPending && form && (
          <OmniArtifactFormCard
            // Keyed by the form: the thread can move on to its next question
            // while this is open, and the card's own stepping state must not
            // carry over from the last one.
            key={form.id}
            artifact={form}
            threadId={threadId}
            stagedValues={draft?.formId === form.id ? draft.values : undefined}
            isSubmitting={submitForm.isPending}
            onStageValues={(values) => onDraft({ formId: form.id, values })}
            onAdvance={(answers) =>
              submitForm.mutate(
                { artifactId: form.id, values: answers },
                {
                  onSuccess: onAnswered,
                  onError: (error) =>
                    errorToast(apiErrorMessage(error) ?? "Couldn't send your answer."),
                },
              )
            }
            // Nothing to step back to: the deck shows one form, where the
            // thread's own carousel pages between several.
            onPrevArtifact={() => {}}
            onDismiss={onClose}
            // Expand opens the whole form on the canvas, beside the list. The
            // dialog's overlay would cover it, so the dialog goes.
            onOpenedOnCanvas={onClose}
          />
        )}

        {/* An approval: what is being decided, with the buttons that decide
            it. Both come off the artifact — the biscuit is the summary the
            server rendered for this, and its action rows are drawn from the
            declarative list every surface draws its controls from — so this
            knows nothing about which kind it is holding. */}
        {!isPending && !form && approval && (
          <div className="flex flex-col gap-3">
            {why ? <p className={WHY_TEXT}>{toPlainText(why)}</p> : null}
            <ArtifactBiscuit artifact={approval} threadId={threadId} onOpenedOnCanvas={onClose} />
            <ContinueInThread threadId={threadId} onClose={onClose} />
          </div>
        )}

        {!isPending && !form && !approval && (
          <div className="flex flex-col gap-3">
            <p className={WHY_TEXT}>
              {why ? toPlainText(why) : "Okthx is waiting on you in this task's conversation."}
            </p>
            <div className="flex gap-2">
              <Input
                autoFocus
                placeholder={REPLY_HINT}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void sendReply();
                }}
              />
              <Button
                disabled={!written || isSending}
                data-statsig-name="tasks-answer-send"
                onClick={() => void sendReply()}
              >
                {SEND}
              </Button>
            </div>
            {sendError && <p className="text-xs text-destructive">{sendError.message}</p>}
            <ContinueInThread threadId={threadId} onClose={onClose} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
